import { Add, CheckCircle, Warning } from "@mui/icons-material";
import {
  Box,
  Chip,
  Fade,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { t } from "i18next";
import { useSelector } from "react-redux";
import Selecto from "react-selecto";
import { SelectionAddedRemovedProps } from "../../Pages/WeeklyPlanner";
import {
  ScreenSelection,
  selectPlaylists,
  selectStatus,
} from "../../store/reducers/dailyContentPlaylistReducer";
import { stringToColor } from "../../../../utils/string";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  IGroup,
  IOrganizationUnit,
  LoadingStatus,
} from "../../../../../types/NendaTypes";
import ScreenSelectFilter, { FilterKey } from "../ScreenSelectFilter";

const days = [
  "common.days_of_week.monday",
  "common.days_of_week.tuesday",
  "common.days_of_week.wednesday",
  "common.days_of_week.thursday",
  "common.days_of_week.friday",
  "common.days_of_week.saturday",
  "common.days_of_week.sunday",
];

const WeekSchedule = ({
  selectoRef,
  screens,
  onChange,
  groups,
  premises,
}: {
  selectoRef: React.RefObject<Selecto>;
  screens: ScreenSelection[];
  groups: IGroup[];
  premises: IOrganizationUnit[];
  onChange: (items: SelectionAddedRemovedProps) => void;
}) => {
  return (
    <MultiSelect
      screens={screens}
      onChange={onChange}
      selectoRef={selectoRef}
      groups={groups}
      premises={premises}
    />
  );
};

const MultiSelect = ({
  selectoRef,
  screens,
  onChange,
  groups,
  premises,
}: {
  selectoRef: React.RefObject<Selecto>;
  screens: ScreenSelection[];
  onChange: (items: SelectionAddedRemovedProps) => void;
  groups: IGroup[];
  premises: IOrganizationUnit[];
}) => {
  const [selectedDay, setSelectedDay] = useState(1); // Default to Monday
  const [filters, setFilters] = useState<{
    group: string[];
    premise: string[];
  }>({
    group: [], // Multiple values allowed
    premise: [], // Premise is single select but an array is still used due to easier handling
  });

  const dailyContentPlaylists = useSelector(selectPlaylists);
  const playlistLoadingStatus = useSelector(selectStatus);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const cubeHeight = screens.length > 3 ? "5rem" : "7rem";
  const VISIBLE_GROUP_CHIPS = 1;

  const handleFilterChange = (key: FilterKey, value: string[]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const filteredScreens = useMemo(() => {
    let list = screens;

    // GROUP facet uses AND logic
    if (filters.group.length) {
      list = list.filter((s) =>
        filters.group.every((gid) => s.groups.includes(gid))
      );
    }

    // PREMISE facet uses OR logic
    if (filters.premise.length) {
      list = list.filter((s) => filters.premise.includes(s.premise.id));
    }

    return list;
  }, [screens, filters]);

  const prevFilteredRef = useRef<ScreenSelection[] | undefined>(undefined);

  useEffect(() => {
    if (!prevFilteredRef.current) {
      prevFilteredRef.current = filteredScreens;
      return;
    }

    const prevIds = new Set(prevFilteredRef.current.map((s) => s._id));
    const currIds = new Set(filteredScreens.map((s) => s._id));

    const removedIds = [...prevIds].filter((id) => !currIds.has(id));
    const removedScreens = screens.filter((s) => removedIds.includes(s._id));

    if (removedScreens.length) {
      onChange({
        added: [],
        removed: removedScreens.flatMap((s) =>
          s.selected.map((day) => ({ id: s._id, day }))
        ),
      });
    }

    prevFilteredRef.current = filteredScreens;
  }, [filteredScreens, screens, onChange]);

  const sortedScreens = useMemo(() => {
    return [...filteredScreens].sort((a, b) => {
      return a.premise.id.localeCompare(b.premise.id);
    });
  }, [filteredScreens]);

  return (
    <Fade appear in={screens.length > 0}>
      <Box className="selecto-window">
        <Selecto
          ref={selectoRef}
          container={document.body}
          dragContainer={".selecto-window"}
          selectableTargets={[".selecto-area .cube"]}
          selectByClick={true}
          selectFromInside={true}
          preventDefault={true}
          continueSelect={true}
          toggleContinueSelect={"shift"}
          keyContainer={window}
          hitRate={0}
          onSelect={(e) => {
            const added = e.added.map((el) => {
              const [id, day] = el.id.split("_");
              return { id, day: Number(day) };
            });
            const removed = e.removed.map((el) => {
              const [id, day] = el.id.split("_");
              return { id, day: Number(day) };
            });

            onChange({ added, removed });
          }}
        />
        <Box
          sx={(theme) => ({
            display: "flex",
            width: "100%",
            alignItems: "center",
            padding: "1rem",
          })}
        >
          <Box
            sx={(theme) => ({
              width: "225px",
              px: "1rem",
              [theme.breakpoints.down("lg")]: { width: "150px" },
            })}
          >
            <Stack direction={"row"} gap="1rem">
              <Typography
                variant="subtitle1"
                sx={(theme) => ({
                  fontSize: "1rem",
                  [theme.breakpoints.down("lg")]: { fontSize: "0.8rem" },
                })}
                textAlign={"left"}
              >
                {t("common.screens")}
              </Typography>
              <ScreenSelectFilter
                groups={groups}
                premises={premises.length > 1 ? premises : []}
                values={filters}
                onChange={handleFilterChange}
              />
            </Stack>
          </Box>
          <Box
            sx={(theme) => ({
              display: "flex",
              width: "100%",
              gap: "0.5rem",
              [theme.breakpoints.up("lg")]: { display: "none" },
            })}
          >
            <FormControl fullWidth sx={{ display: { lg: "none" } }}>
              <Select
                value={selectedDay}
                onChange={(e) => {
                  if (e.target.value && typeof e.target.value === "number") {
                    setSelectedDay(e.target.value);
                  }
                }}
                displayEmpty
                inputProps={{ "aria-label": "Select day" }}
              >
                {days.map((day, index) => (
                  <MenuItem key={day} value={index + 1}>
                    {t(day)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={(theme) => ({
              display: "flex",
              width: "100%",
              gap: "0.5rem",
              [theme.breakpoints.down("lg")]: { display: "none" },
            })}
          >
            {days.map((day) => (
              <Box
                key={day}
                sx={(theme) => ({
                  transition: "background 0.2s ease",
                  width: "100%",
                  boxShadow: "8px 7px 47px 7px rgba(0,0,0,0.05)",
                  height: "auto",
                  padding: "0.5rem",
                  background: theme.palette.transparent.blue,
                  borderRadius: "8px",
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "thin",
                })}
              >
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    fontSize: "0.7rem",
                    color: theme.palette.primary.main,
                  })}
                >
                  {t(day)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          className="selecto-area"
          sx={(theme) => ({
            width: "100%",
            background: theme.palette.background.paper,

            padding: "1rem",
            borderRadius: "8px",
            "& .cube": {
              width: "100%",
              boxShadow: "8px 7px 47px 7px rgba(0,0,0,0.05)",
              height: cubeHeight,
              padding: "1rem 0.5rem",
              borderRadius: "8px",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: "thin",
              cursor: "pointer",
            },
          })}
        >
          {sortedScreens.length === 0 ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body1">
                {t("customerportal.pages.schedule.empty_result.description")}
              </Typography>
            </Box>
          ) : (
            sortedScreens.map((screen) => {
              const screenGroupIds = screen.groups;
              const visibleGroups = groups
                .filter((group) => screenGroupIds.includes(group._id))
                .slice(0, VISIBLE_GROUP_CHIPS);

              const hiddenGroups = groups
                .filter((group) => screenGroupIds.includes(group._id))
                .slice(VISIBLE_GROUP_CHIPS);

              return (
                <Box
                  key={screen._id}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    my: "1rem",
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      px: "1rem",
                      width: "225px",
                      [theme.breakpoints.down("lg")]: { width: "150px" },
                    })}
                  >
                    <Typography
                      variant="body1"
                      sx={(theme) => ({
                        fontSize: "1rem",
                        [theme.breakpoints.down("lg")]: { fontSize: "0.8rem" },
                      })}
                      textAlign={"left"}
                    >
                      {screen.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      {visibleGroups.map((group) => {
                        return (
                          <Tooltip key={group._id} title={group.name} arrow>
                            <Box
                              key={group._id}
                              component="div"
                              sx={{
                                display: "inline-block",
                                px: 1,
                                py: 0.5,
                                maxWidth: () => {
                                  return isMobile ? "80px" : "120px";
                                },
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "0.8rem",
                                lineHeight: 1,
                                bgcolor: (theme) =>
                                  group.premise
                                    ? theme.palette.primary.main
                                    : theme.palette.secondary.main,
                                color: (theme) =>
                                  theme.palette.primary.contrastText,
                                borderRadius: "4px",
                              }}
                            >
                              {group.name}
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {hiddenGroups.length > 0 && (
                        <Tooltip
                          arrow
                          title={
                            <Stack direction="column">
                              {hiddenGroups
                                .map((group) => group.name)
                                .join(", ")}
                            </Stack>
                          }
                        >
                          <Chip
                            size="small"
                            label={`+${hiddenGroups.length}`}
                            variant="outlined"
                            sx={{ fontWeight: 400, display: "inline-flex" }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", width: "100%", gap: "0.5rem" }}>
                    {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                      const isSelected = screens.find(
                        (s) => s._id === screen._id && s.selected.includes(day)
                      );
                      const currentPlaylist = screens.find(
                        (s) => s._id === screen._id
                      )?.schedule[day];
                      const dailyContentPlaylist = dailyContentPlaylists.find(
                        (p) => p._id === currentPlaylist
                      );

                      if (isMobile && day !== selectedDay) {
                        return null;
                      }

                      return (
                        <Box
                          key={day}
                          id={`${screen._id + "_" + day}`}
                          className="cube"
                          sx={(theme) => ({
                            overflow: "hidden",
                            background: isSelected
                              ? theme.palette.primary.light
                              : currentPlaylist
                                ? theme.palette.gradient.darkBlue
                                : theme.palette.common.white,
                            transition: "background 0.2s ease",
                            position: "relative",
                            outline: isSelected
                              ? `3px solid ${theme.palette.primary.light}`
                              : "none",
                            "& svg": {
                              color: isSelected
                                ? theme.palette.common.white
                                : currentPlaylist
                                  ? theme.palette.common.white
                                  : theme.palette.primary.light,
                            },
                          })}
                        >
                          {currentPlaylist && (
                            <Box
                              sx={{
                                background: isSelected
                                  ? "transparent"
                                  : stringToColor(currentPlaylist),
                                p: "0.15rem",
                                position: "absolute",
                                top: "0",
                                left: "0",
                                bottom: "0",
                              }}
                            />
                          )}
                          {currentPlaylist && !isSelected ? (
                            <>
                              {!dailyContentPlaylist &&
                                playlistLoadingStatus !==
                                  LoadingStatus.LOADING && (
                                  <Tooltip
                                    title={t(
                                      "customerportal.pages.week_planner.missing_playlist_tooltip"
                                    )}
                                  >
                                    <Warning
                                      sx={(theme) => ({
                                        position: "absolute",
                                        top: "0.5rem",
                                        right: "0.5rem",
                                        height: "1rem",
                                        fill: theme.palette.warning.light,
                                      })}
                                    />
                                  </Tooltip>
                                )}
                              <Typography
                                fontStyle={
                                  !dailyContentPlaylist ? "italic" : "normal"
                                }
                                textAlign={"center"}
                                variant="body1"
                                fontSize="0.7rem"
                                width={"100%"}
                                overflow="hidden"
                              >
                                {dailyContentPlaylist?.name || "Unknown"}
                              </Typography>
                            </>
                          ) : (
                            <>{isSelected ? <CheckCircle /> : <Add />}</>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export default WeekSchedule;
